import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { platformConfigAPI } from '../../services/api';
import { Card, Button, Spinner } from '../UI';
import { HiOutlineGift, HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi';

const defaultForm = {
  isEnabled: true,
  rewardType: 'fixed',
  rewardValue: 200,
  coinValueInRupees: 1,
};

/**
 * SuperAdmin → Settings → Refer & Earn
 *
 * Controls the customer-to-customer referral reward: how much a referring org
 * earns (flat ₹ or % of the referred org's plan) when the referred org
 * completes its first paid payment, and what one coin is worth in ₹.
 */
const ReferAndEarnSettings = () => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['sa-platform-config'],
    queryFn: async () => (await platformConfigAPI.getConfig()).data?.data,
  });

  useEffect(() => {
    if (data && !form) {
      setForm({ ...defaultForm, ...(data.referAndEarn || {}) });
    }
  }, [data, form]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await platformConfigAPI.updateConfig({ referAndEarn: form });
      queryClient.invalidateQueries(['sa-platform-config']);
      toast.success('Refer & Earn settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || !form) {
    return (
      <Card padding="p-6" className="flex items-center justify-center">
        <Spinner />
      </Card>
    );
  }

  return (
    <Card padding="p-4 sm:p-6" className="space-y-5">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
          <HiOutlineGift className="w-5 h-5 text-green-600" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-bold text-gray-900">Refer & Earn</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            When an organization refers another org and that org completes its first paid
            payment, the referring org earns coins. Set the reward and what one coin is
            worth here.
          </p>
        </div>
        <button
          type="button"
          onClick={() => handleChange('isEnabled', !form.isEnabled)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
            form.isEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
          }`}
        >
          {form.isEnabled ? <HiOutlineEye className="w-4 h-4" /> : <HiOutlineEyeOff className="w-4 h-4" />}
          {form.isEnabled ? 'Enabled' : 'Disabled'}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Reward Type</label>
          <select
            value={form.rewardType}
            onChange={(e) => handleChange('rewardType', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option value="fixed">Fixed Amount (₹)</option>
            <option value="percentage">Percentage (%)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">
            Reward Value {form.rewardType === 'percentage' ? '(%)' : '(₹)'}
          </label>
          <input
            type="number"
            min="0"
            step={form.rewardType === 'percentage' ? '1' : '0.01'}
            value={form.rewardValue}
            onChange={(e) => handleChange('rewardValue', parseFloat(e.target.value) || 0)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">1 Coin = ₹</label>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={form.coinValueInRupees}
            onChange={(e) => handleChange('coinValueInRupees', parseFloat(e.target.value) || 1)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
      </div>

      <p className="text-xs text-gray-500">
        Example: a referred org pays ₹{form.rewardType === 'percentage' ? '1,000' : (form.rewardValue || 0).toLocaleString('en-IN')}
        {form.rewardType === 'percentage'
          ? ` → referrer earns ${form.rewardValue}% = ₹${Math.round(1000 * (form.rewardValue / 100))} = ${Math.round((1000 * (form.rewardValue / 100)) / (form.coinValueInRupees || 1))} coins`
          : ` for their plan → referrer earns ₹${form.rewardValue} = ${Math.round(form.rewardValue / (form.coinValueInRupees || 1))} coins`}
        .
      </p>

      <div className="flex justify-end pt-2 border-t border-gray-100">
        <Button variant="primary" onClick={handleSave} loading={saving} disabled={saving}>
          Save Settings
        </Button>
      </div>
    </Card>
  );
};

export default ReferAndEarnSettings;
